import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHero from '../components/PageHero';

const sectionHeadingStyle = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  letterSpacing: '0.05em',
};

const offers = [
  {
    id: 'referral-program',
    title: 'Referral Program',
    image: '/Referall.png',
    alt: 'Referral program offer',
    cardClass: 'max-w-3xl',
    heading: 'Think You Can Sell Anything?',
    subheading: 'Easy Commission Based Opportunity.',
    description:
      'Earn 20% for every business or customer you refer who buys from us. That means $100 for every $500 someone you refer spends with us. A lot of people have made quick cash through referrals. You don\'t have to do any of the work, we do it all. Your sales skills are what we value and look for.',
  },
  {
    id: 'client-discounts',
    title: 'Monthly Maintenance',
    image: '/monthlymaintenance.png',
    alt: 'Monthly website maintenance offer',
    cardClass: 'max-w-xl md:max-w-2xl',
    sectionClass: 'mt-16 md:mt-24 lg:mt-28',
  },
];

export default function Offers() {
  const location = useLocation();
  const [expandedOffer, setExpandedOffer] = useState(null);

  const closeExpanded = useCallback(() => setExpandedOffer(null), []);

  useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!location.hash) return undefined;

    const id = location.hash.replace('#', '');
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!expandedOffer) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeExpanded();
    };

    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [expandedOffer, closeExpanded]);

  const activeOffer = offers.find((o) => o.title === expandedOffer);

  return (
    <PageLayout>
      <PageHero
        eyebrow="Specials"
        title="Exclusive Offers and Deals"
        subtitle="Save on maintenance and earn rewards when you refer someone who needs a site."
        note="Tap an offer to expand it"
        className="pb-8 md:pb-10"
        maxWidth="max-w-5xl"
      />

      <section className="px-4 md:px-8 pb-20">
        <div className="max-w-5xl mx-auto">

          <div className="flex flex-col gap-8 md:gap-10 mb-12 max-w-3xl mx-auto">
            {offers.map((offer) => (
              <div
                key={offer.title}
                id={offer.id}
                className={`w-full scroll-mt-32 ${offer.sectionClass || ''}`}
              >
                {offer.heading && (
                  <div className="text-center mb-6 md:mb-8 max-w-3xl mx-auto px-2">
                    <h2
                      className="text-2xl md:text-3xl font-bold mb-2 text-gray-700"
                      style={sectionHeadingStyle}
                    >
                      {offer.heading}
                    </h2>
                    <p
                      className="text-lg md:text-xl font-semibold mb-6"
                      style={{ fontFamily: "'Poppins', sans-serif", color: '#ff7a59' }}
                    >
                      {offer.subheading}
                    </p>

                    <div
                      className="relative rounded-2xl p-6 md:p-8 text-left shadow-xl overflow-hidden"
                      style={{
                        backgroundColor: '#fffcf0',
                        border: '2px solid rgba(255, 122, 89, 0.45)',
                        boxShadow: '0 12px 40px rgba(255, 122, 89, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                      }}
                    >
                      <div
                        className="absolute top-0 left-0 w-full h-1"
                        style={{
                          background: 'linear-gradient(90deg, #ff7a59 0%, #accbff 50%, #ff7a59 100%)',
                        }}
                      />
                      <div
                        className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-20"
                        style={{ backgroundColor: '#ff7a59' }}
                      />
                      <div
                        className="absolute -bottom-12 -left-12 w-36 h-36 rounded-full opacity-15"
                        style={{ backgroundColor: '#accbff' }}
                      />

                      <div className="relative z-10">
                        <span
                          className="inline-block mb-4 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-white shadow-md"
                          style={{
                            backgroundColor: '#ff7a59',
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          20% commission · paid per referral
                        </span>

                        <div className="flex flex-wrap gap-3 mb-5">
                          <div
                            className="flex-1 min-w-[140px] rounded-xl px-4 py-3 text-center border-2"
                            style={{
                              borderColor: '#ff7a59',
                              backgroundColor: 'rgba(255, 122, 89, 0.08)',
                            }}
                          >
                            <p
                              className="text-2xl md:text-3xl font-bold leading-none"
                              style={{ color: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
                            >
                              20%
                            </p>
                            <p
                              className="text-xs mt-1 font-semibold uppercase tracking-wide text-gray-600"
                              style={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                              on every sale
                            </p>
                          </div>
                          <div
                            className="flex-1 min-w-[140px] rounded-xl px-4 py-3 text-center border-2"
                            style={{
                              borderColor: '#accbff',
                              backgroundColor: 'rgba(172, 203, 255, 0.35)',
                            }}
                          >
                            <p
                              className="text-2xl md:text-3xl font-bold leading-none"
                              style={{ color: '#292929', fontFamily: "'Poppins', sans-serif" }}
                            >
                              $100
                            </p>
                            <p
                              className="text-xs mt-1 font-semibold uppercase tracking-wide text-gray-600"
                              style={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                              per $500 spent
                            </p>
                          </div>
                        </div>

                        <p
                          className="text-base md:text-lg leading-relaxed text-gray-700"
                          style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                          {offer.description}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <article
                  className={`rounded-2xl overflow-hidden border border-white/30 bg-white/15 backdrop-blur-sm shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl cursor-pointer group mx-auto w-full ${offer.cardClass}`}
                  onClick={() => setExpandedOffer(offer.title)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedOffer(offer.title);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`${offer.title}. Click to expand`}
                >
                <div className="relative">
                  <img
                    src={offer.image}
                    alt={offer.alt}
                    className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                  <div
                    className="absolute inset-0 flex items-end justify-center pb-4 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <span
                      className="px-4 py-2 rounded-full text-white text-sm font-medium shadow-lg"
                      style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
                    >
                      Click to expand
                    </span>
                  </div>
                </div>
                </article>

                {offer.title === 'Referral Program' && (
                  <div
                    className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
                  >
                    <p
                      className="text-base md:text-lg font-semibold text-gray-700"
                      style={{ fontFamily: "'Poppins', sans-serif" }}
                    >
                      Have a referral?
                    </p>
                    <Link to="/contactme" className="group">
                      <button
                        className="px-8 py-3 text-white font-medium rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
                        style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
                      >
                        Contact Us
                      </button>
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center max-w-xl mx-auto">
            <p
              className="text-base md:text-lg mb-6"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            >
              Want to claim an offer or ask about a custom deal? Book a free consult and we will walk you through it.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/book" className="group">
                <button
                  className="px-8 py-3 text-white font-medium rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
                  style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
                >
                  Book a consultation
                </button>
              </Link>
              <Link to="/contactme" className="group">
                <button
                  className="px-8 py-3 font-medium rounded-full border-2 border-[#ff7a59] bg-white/40 backdrop-blur-sm text-gray-700 transition-all duration-300 transform hover:scale-105"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Contact us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {activeOffer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/75 backdrop-blur-sm"
          onClick={closeExpanded}
          role="dialog"
          aria-modal="true"
          aria-label={`${activeOffer.title} expanded`}
        >
          <button
            type="button"
            onClick={closeExpanded}
            className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-white/90 text-gray-700 text-xl leading-none shadow-lg hover:bg-white transition-colors"
            aria-label="Close expanded view"
          >
            ×
          </button>
          <img
            src={activeOffer.image}
            alt={activeOffer.alt}
            className="max-h-[90vh] max-w-full w-auto rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

    </PageLayout>
  );
}
